import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Music.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MusicPlayer from '../../components/MusicPlayer/MusicPlayer';
import musicService from '../../services/musicService';
import { useAuth } from '../../hooks/useAuth';

export default function Music() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [currentPlayingTrack, setCurrentPlayingTrack] = useState(null);
  const [playError, setPlayError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAllTracks, setShowAllTracks] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());
  const [showFavorites, setShowFavorites] = useState(true); // State để thu gọn/mở rộng favorites
  const [showFavoritesPage, setShowFavoritesPage] = useState(false); // State để hiển thị trang favorite
  const [showPopup, setShowPopup] = useState(false); // State để hiển thị popup
  const [popupUrl, setPopupUrl] = useState(''); // URL để embed trong popup
  const [popupType, setPopupType] = useState(''); // Loại popup (youtube, spotify, soundcloud)
  const [isPopupMinimized, setIsPopupMinimized] = useState(false); // State để thu nhỏ popup
  const [currentPopupTrack, setCurrentPopupTrack] = useState(null); // Track đang phát trong popup
  const audioRef = useRef(null);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Reload favorites when user changes
  useEffect(() => {
    const loadFavorites = async () => {
      const currentUser = user || JSON.parse(localStorage.getItem('user') || 'null');
      const token = localStorage.getItem('token');
      if (currentUser && token) {
        try {
          const favoritesData = await musicService.getFavorites();
          setFavorites(Array.isArray(favoritesData) ? favoritesData : []);
        } catch (error) {
          console.warn('Error loading favorites:', error);
          setFavorites([]);
        }
      } else {
        setFavorites([]);
      }
    };
    loadFavorites();
  }, [user]);

  // Filter tracks when search/category/platform changes
  useEffect(() => {
    filterTracks();
    // Reset showAllTracks khi filter thay đổi
    if (searchQuery.trim() || selectedCategory !== 'all' || selectedPlatform !== 'all') {
      setShowAllTracks(false);
    }
  }, [searchQuery, selectedCategory, selectedPlatform, tracks]);

  // Handle ESC key to close popup or minimize
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showPopup) {
        if (isPopupMinimized) {
          setShowPopup(false);
          setIsPopupMinimized(false);
        } else {
          setIsPopupMinimized(true);
        }
      }
    };

    if (showPopup) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when popup is open and not minimized
      if (!isPopupMinimized) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showPopup, isPopupMinimized]);

  // Shuffle và limit tracks để hiển thị
  const getDisplayTracks = () => {
    if (showAllTracks || searchQuery.trim() || selectedCategory !== 'all' || selectedPlatform !== 'all') {
      // Nếu đang filter hoặc search, hiển thị tất cả kết quả
      return filteredTracks;
    }
    
    // Nếu không filter, chỉ hiển thị 10 bài ngẫu nhiên
    if (filteredTracks.length <= 10) {
      return filteredTracks;
    }
    
    // Shuffle và lấy 10 bài đầu
    const shuffled = [...filteredTracks].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 10);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Try to load from API, but fallback to sample data if 404 or other errors
      let tracksData = [];
      let categoriesData = [];
      let platformsData = [];

      try {
        tracksData = await musicService.getAllTracks();
        if (!Array.isArray(tracksData)) {
          tracksData = [];
        }
        // Log số lượng tracks đã load
        if (tracksData.length > 0) {
          console.log(`✅ Loaded ${tracksData.length} tracks from API`);
        }
      } catch (error) {
        console.warn('Could not load tracks from API, using sample data:', error);
        tracksData = [];
      }

      try {
        categoriesData = await musicService.getCategories();
        if (!Array.isArray(categoriesData)) {
          categoriesData = [];
        }
      } catch (error) {
        console.warn('Could not load categories from API:', error);
        categoriesData = [];
      }

      try {
        platformsData = await musicService.getPlatforms();
        if (!Array.isArray(platformsData)) {
          platformsData = [];
        }
      } catch (error) {
        console.warn('Could not load platforms from API:', error);
        platformsData = [];
      }

      // If no tracks from API, use sample data
      if (tracksData.length === 0) {
        tracksData = getSampleTracks();
      }

      // If no categories from API, use sample categories
      if (categoriesData.length === 0) {
        categoriesData = getSampleCategories();
      }

      // If no platforms from API, use sample platforms
      if (platformsData.length === 0) {
        platformsData = getSamplePlatforms();
      }

      setTracks(tracksData);
      setCategories(categoriesData);
      setPlatforms(platformsData);

      // Load favorites if user is logged in - check from multiple sources
      const currentUser = user || JSON.parse(localStorage.getItem('user') || 'null');
      const token = localStorage.getItem('token');
      if (currentUser && token) {
        try {
          const favoritesData = await musicService.getFavorites();
          setFavorites(Array.isArray(favoritesData) ? favoritesData : []);
          console.log('Loaded favorites:', favoritesData.length);
        } catch (error) {
          console.warn('Error loading favorites:', error);
          setFavorites([]);
        }
      } else {
        setFavorites([]);
      }
    } catch (error) {
      console.error('Error loading music data:', error);
      // Fallback to sample data if API fails completely
      setTracks(getSampleTracks());
      setCategories([]);
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  const filterTracks = () => {
    let filtered = [...tracks];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(track =>
        track.title?.toLowerCase().includes(query) ||
        track.artist?.toLowerCase().includes(query) ||
        track.description?.toLowerCase().includes(query)
      );
    }

    // Filter by category - hỗ trợ cả snake_case và camelCase, convert sang string để so sánh UUID
    if (selectedCategory !== 'all') {
      const categoryIdStr = String(selectedCategory);
      const beforeCount = filtered.length;
      filtered = filtered.filter(track => {
        const trackCategoryId = track.category_id || track.categoryId;
        if (!trackCategoryId) return false;
        // Convert cả hai sang string để đảm bảo match UUID
        return String(trackCategoryId) === categoryIdStr;
      });
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Category filter:', {
          selectedCategory: categoryIdStr,
          beforeCount,
          afterCount: filtered.length,
          tracksWithCategory: tracks.filter(t => t.category_id || t.categoryId).length
        });
      }
    }

    // Filter by platform - hỗ trợ cả snake_case và camelCase, convert sang string để so sánh UUID
    if (selectedPlatform !== 'all') {
      const platformIdStr = String(selectedPlatform);
      const beforeCount = filtered.length;
      filtered = filtered.filter(track => {
        const trackPlatformId = track.platform_id || track.platformId;
        if (!trackPlatformId) return false;
        // Convert cả hai sang string để đảm bảo match UUID
        return String(trackPlatformId) === platformIdStr;
      });
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Platform filter:', {
          selectedPlatform: platformIdStr,
          beforeCount,
          afterCount: filtered.length,
          tracksWithPlatform: tracks.filter(t => t.platform_id || t.platformId).length
        });
      }
    }

    setFilteredTracks(filtered);
  };

  const handleGetRecommendations = async () => {
    try {
      setLoading(true);
      const recs = await musicService.getRecommendations(6, true);
      // Limit to 6 recommendations
      const limitedRecs = (recs || []).slice(0, 6);
      setRecommendations(limitedRecs);
      setShowRecommendations(true);
      setShowAllTracks(false); // Ẩn phần All Tracks khi hiển thị recommendations
    } catch (error) {
      console.error('Error getting recommendations:', error);
      alert('Không thể tải đề xuất. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (track) => {
    // Check user from multiple sources
    const currentUser = user || JSON.parse(localStorage.getItem('user') || 'null');
    const token = localStorage.getItem('token');
    
    if (!currentUser || !token) {
      alert('Vui lòng đăng nhập để thêm vào yêu thích');
      navigate('/login');
      return;
    }

    try {
      const trackId = track.track_id || track.id;
      if (!trackId) {
        console.error('Track ID not found:', track);
        alert('Không tìm thấy ID của bài hát');
        return;
      }

      // Check if already favorited - check multiple possible field names
      const favorite = favorites.find(fav => {
        const favTrackId = fav.track_id || fav.music_track_id || fav.musicTrackId;
        return String(favTrackId) === String(trackId);
      });

      if (favorite) {
        // Remove from favorites - use trackId directly
        await musicService.removeFromFavorites(trackId);
        // Update state directly without reloading from server
        setFavorites(prevFavorites => 
          prevFavorites.filter(fav => {
            const favTrackId = fav.track_id || fav.music_track_id || fav.musicTrackId;
            return String(favTrackId) !== String(trackId);
          })
        );
      } else {
        // Add to favorites - use trackId directly
        const result = await musicService.addToFavorites(trackId);
        // Update state directly without reloading from server
        // If API returns the favorite object, add it; otherwise create a simple one
        if (result && result.id) {
          setFavorites(prevFavorites => [...prevFavorites, result]);
        } else {
          // Create a simple favorite object if API doesn't return one
          setFavorites(prevFavorites => [...prevFavorites, {
            id: `temp-${trackId}-${Date.now()}`,
            track_id: trackId,
            music_track_id: trackId,
            musicTrackId: trackId
          }]);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      if (error.response?.status === 401) {
        alert('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else {
        alert(error.response?.data?.message || 'Không thể cập nhật yêu thích. Vui lòng thử lại.');
      }
    }
  };

  const isFavorited = (trackId) => {
    if (!trackId || favorites.length === 0) return false;
    return favorites.some(fav => {
      const favTrackId = fav.track_id || fav.music_track_id || fav.musicTrackId;
      return String(favTrackId) === String(trackId);
    });
  };

  // Convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Extract video ID from various YouTube URL formats
    let videoId = '';
    
    // Regular YouTube URL: https://www.youtube.com/watch?v=VIDEO_ID
    const match1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    if (match1) {
      videoId = match1[1];
    }
    
    // YouTube embed URL: https://www.youtube.com/embed/VIDEO_ID
    const match2 = url.match(/youtube\.com\/embed\/([^&\n?#]+)/);
    if (match2) {
      videoId = match2[1];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    
    return url;
  };

  // Convert Spotify URL to embed URL
  const getSpotifyEmbedUrl = (url) => {
    if (!url) return '';
    
    // Spotify track: https://open.spotify.com/track/TRACK_ID
    const trackMatch = url.match(/spotify\.com\/track\/([a-zA-Z0-9]+)/);
    if (trackMatch) {
      return `https://open.spotify.com/embed/track/${trackMatch[1]}?utm_source=generator`;
    }
    
    // Spotify album: https://open.spotify.com/album/ALBUM_ID
    const albumMatch = url.match(/spotify\.com\/album\/([a-zA-Z0-9]+)/);
    if (albumMatch) {
      return `https://open.spotify.com/embed/album/${albumMatch[1]}?utm_source=generator`;
    }
    
    // Spotify playlist: https://open.spotify.com/playlist/PLAYLIST_ID
    const playlistMatch = url.match(/spotify\.com\/playlist\/([a-zA-Z0-9]+)/);
    if (playlistMatch) {
      return `https://open.spotify.com/embed/playlist/${playlistMatch[1]}?utm_source=generator`;
    }
    
    return url;
  };

  // Convert SoundCloud URL to embed URL
  const getSoundCloudEmbedUrl = (url) => {
    if (!url) return '';
    
    // SoundCloud URL format: https://soundcloud.com/user/track-name
    // Return the URL as-is, we'll use it in the SoundCloud widget URL
    return url;
  };

  const handlePlayTrack = async (track) => {
    try {
      setPlayError(null);
      setCurrentPlayingTrack(track);
      
      // Lấy URL ưu tiên: url > platform_url > embed_url
      let audioUrl = track.url || track.platform_url || track.embed_url;
      
      if (!audioUrl) {
        setPlayError('Không tìm thấy link phát nhạc cho bài này');
        return;
      }

      // Kiểm tra nếu đang phát cùng track thì pause
      if (audioRef.current && currentPlayingTrack?.id === track.id && !audioRef.current.paused) {
        audioRef.current.pause();
        setIsPlaying(false);
        return;
      }

      if (audioRef.current) {
        // Xử lý các nền tảng đặc biệt - mở popup thay vì tab mới
        const platformName = track.platform_name?.toLowerCase() || '';
        
        // YouTube - mở popup
        if (audioUrl.includes('youtube.com') || audioUrl.includes('youtu.be') || platformName === 'youtube') {
          const youtubeUrl = track.platform_url || audioUrl;
          const embedUrl = getYouTubeEmbedUrl(youtubeUrl);
          setPopupUrl(embedUrl);
          setPopupType('youtube');
          setCurrentPopupTrack(track);
          setIsPopupMinimized(false);
          setShowPopup(true);
          return;
        }

        // Spotify - mở popup
        if (audioUrl.includes('spotify.com') || platformName === 'spotify') {
          const spotifyUrl = track.platform_url || audioUrl;
          const embedUrl = getSpotifyEmbedUrl(spotifyUrl);
          setPopupUrl(embedUrl);
          setPopupType('spotify');
          setCurrentPopupTrack(track);
          setIsPopupMinimized(false);
          setShowPopup(true);
          return;
        }

        // SoundCloud - mở popup
        if (audioUrl.includes('soundcloud.com') || platformName === 'soundcloud') {
          const soundcloudUrl = track.platform_url || audioUrl;
          const embedUrl = getSoundCloudEmbedUrl(soundcloudUrl);
          setPopupUrl(embedUrl);
          setPopupType('soundcloud');
          setCurrentPopupTrack(track);
          setIsPopupMinimized(false);
          setShowPopup(true);
          return;
        }

        // Kiểm tra xem URL có phải là file audio hợp lệ không
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'];
        const isDirectAudioFile = audioExtensions.some(ext => audioUrl.toLowerCase().endsWith(ext));
        
        // Nếu không phải file audio trực tiếp và không phải URL http/https, có thể là đường dẫn local không tồn tại
        if (!isDirectAudioFile && !audioUrl.startsWith('http://') && !audioUrl.startsWith('https://')) {
          // Thử với đường dẫn public
          if (audioUrl.startsWith('/')) {
            // Đã là đường dẫn tuyệt đối, giữ nguyên
          } else {
            // Thêm / nếu thiếu
            audioUrl = '/' + audioUrl;
          }
        }

        // Kiểm tra xem URL có hợp lệ không
        if (!audioUrl.startsWith('http://') && !audioUrl.startsWith('https://') && !audioUrl.startsWith('/')) {
          setPlayError('Link phát nhạc không hợp lệ. Vui lòng kiểm tra lại.');
          return;
        }

        // Load và phát audio
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        
        // Thêm timeout để kiểm tra xem audio có load được không
        const loadTimeout = setTimeout(() => {
          if (audioRef.current && audioRef.current.readyState === 0) {
            setPlayError('Không thể tải nhạc. File có thể không tồn tại hoặc link đã hết hạn.');
            setIsPlaying(false);
          }
        }, 5000);

        try {
          await audioRef.current.play();
          clearTimeout(loadTimeout);
          setIsPlaying(true);
          
          // Tăng play count
          try {
            await musicService.incrementPlayCount(track.id);
          } catch (err) {
            console.warn('Could not increment play count:', err);
          }
        } catch (playError) {
          clearTimeout(loadTimeout);
          console.error('Play error:', playError);
          
          // Kiểm tra loại lỗi
          if (playError.name === 'NotSupportedError') {
            setPlayError('Định dạng audio không được hỗ trợ hoặc file không tồn tại. Vui lòng thử bài khác.');
          } else if (playError.name === 'NotAllowedError') {
            setPlayError('Trình duyệt không cho phép phát nhạc. Vui lòng tương tác với trang trước.');
          } else {
            setPlayError('Không thể phát nhạc. Vui lòng kiểm tra link hoặc thử lại sau.');
          }
          setIsPlaying(false);
        }
      }
    } catch (error) {
      console.error('Error playing track:', error);
      setPlayError('Đã xảy ra lỗi khi phát nhạc. Vui lòng thử lại.');
      setIsPlaying(false);
    }
  };

  // Xử lý khi audio kết thúc
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentPlayingTrack(null);
    };

    const handleError = (e) => {
      console.error('Audio error:', e);
      setPlayError('Không thể tải nhạc. Link có thể không hợp lệ hoặc đã hết hạn.');
      setIsPlaying(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setPlayError(null);
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
    };
  }, []);

  const getSampleTracks = () => {
    return [
      {
        id: '1',
        title: 'Tiếng Mưa Nhẹ',
        artist: 'Âm thanh thiên nhiên',
        url: '/audio/rain-01.mp3',
        cover: 'rain',
        duration: 300,
        description: 'Tiếng mưa nhẹ nhàng giúp thư giãn và ngủ ngon'
      },
      {
        id: '2',
        title: 'Tiếng Sóng Biển',
        artist: 'Âm thanh thiên nhiên',
        url: '/audio/ocean-01.mp3',
        cover: 'ocean',
        duration: 360,
        description: 'Tiếng sóng biển êm đềm mang lại cảm giác bình yên'
      },
      {
        id: '3',
        title: 'Tiếng Chim Hót',
        artist: 'Âm thanh thiên nhiên',
        url: '/audio/birds-01.mp3',
        cover: 'birds',
        duration: 240,
        description: 'Tiếng chim hót trong lành giúp tinh thần sảng khoái'
      },
      {
        id: '4',
        title: 'Nhạc Piano Nhẹ',
        artist: 'Nhạc thư giãn',
        url: '/audio/piano-01.mp3',
        cover: 'piano',
        duration: 280,
        description: 'Nhạc piano nhẹ nhàng cho tâm hồn thư thái'
      }
    ];
  };

  const getSampleCategories = () => {
    return [
      {
        id: 'cat-1',
        name: 'Thiên nhiên',
        icon: '🌿',
        description: 'Âm thanh từ thiên nhiên'
      },
      {
        id: 'cat-2',
        name: 'Nhạc thư giãn',
        icon: '🎵',
        description: 'Nhạc nhẹ nhàng thư giãn'
      },
      {
        id: 'cat-3',
        name: 'Meditation',
        icon: '🧘',
        description: 'Nhạc thiền định'
      },
      {
        id: 'cat-4',
        name: 'Sleep',
        icon: '😴',
        description: 'Nhạc giúp ngủ ngon'
      }
    ];
  };

  const getSamplePlatforms = () => {
    return [
      {
        id: 'platform-1',
        name: 'Local',
        display_name: 'Local Audio',
        icon: '🎧',
        description: 'File audio local'
      },
      {
        id: 'platform-2',
        name: 'YouTube',
        display_name: 'YouTube',
        icon: '▶️',
        description: 'Video từ YouTube'
      },
      {
        id: 'platform-3',
        name: 'Spotify',
        display_name: 'Spotify',
        icon: '🎵',
        description: 'Nhạc từ Spotify'
      }
    ];
  };

  // Lấy hình ảnh cho track, có fallback nếu không có
  const getTrackImage = (track) => {
    if (!track) {
      return getPlaceholderImage({ cover: 'music' });
    }

    // Hỗ trợ cả camelCase (từ C# API) và snake_case (từ database)
    // Ưu tiên: thumbnail_url/thumbnailUrl > cover_image_url/coverImageUrl > placeholder dựa trên cover type
    
    // Kiểm tra thumbnail_url (snake_case) hoặc thumbnailUrl (camelCase)
    const thumbnailUrl = track.thumbnail_url || track.thumbnailUrl;
    if (thumbnailUrl && typeof thumbnailUrl === 'string' && thumbnailUrl.trim() !== '') {
      const trimmedUrl = thumbnailUrl.trim();
      // Kiểm tra xem URL này đã bị lỗi chưa
      if (!failedImages.has(trimmedUrl) && !failedImages.has(thumbnailUrl)) {
        // Validate URL cơ bản
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
          return trimmedUrl;
        }
      }
    }
    
    // Kiểm tra cover_image_url (snake_case) hoặc coverImageUrl (camelCase)
    const coverImageUrl = track.cover_image_url || track.coverImageUrl;
    if (coverImageUrl && typeof coverImageUrl === 'string' && coverImageUrl.trim() !== '') {
      const trimmedUrl = coverImageUrl.trim();
      // Kiểm tra xem URL này đã bị lỗi chưa
      if (!failedImages.has(trimmedUrl) && !failedImages.has(coverImageUrl)) {
        // Validate URL cơ bản
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
          return trimmedUrl;
        }
      }
    }
    
    // Fallback: placeholder images dựa trên cover type
    return getPlaceholderImage(track);
  };

  // Lấy placeholder image dựa trên track
  const getPlaceholderImage = (track) => {
    if (!track) {
      return 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop&q=80';
    }

    const placeholderImages = {
      rain: 'https://images.unsplash.com/photo-1527482797697-8795b05a13fe?w=400&h=400&fit=crop&q=80',
      ocean: 'https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=400&h=400&fit=crop&q=80',
      birds: 'https://images.unsplash.com/photo-1444464666168-49d633b86797?w=400&h=400&fit=crop&q=80',
      piano: 'https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=400&h=400&fit=crop&q=80',
      chill: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=400&fit=crop&q=80',
      music: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop&q=80'
    };
    
    // Nếu có cover type, dùng nó
    if (track.cover && placeholderImages[track.cover]) {
      return placeholderImages[track.cover];
    }
    
    // Nếu không có cover, dùng default
    return placeholderImages.music;
  };

  // Xử lý lỗi khi load hình ảnh
  const handleImageError = (e, track) => {
    if (!e || !e.target) return;
    
    const failedUrl = e.target.src;
    
    // Đánh dấu URL này đã bị lỗi (cả trimmed và original)
    setFailedImages(prev => {
      const newSet = new Set([...prev, failedUrl]);
      if (failedUrl && typeof failedUrl === 'string') {
        newSet.add(failedUrl.trim());
      }
      return newSet;
    });
    
    // Set fallback image
    const fallbackImage = getPlaceholderImage(track || { cover: 'music' });
    
    // Chỉ set lại nếu chưa phải là fallback image và chưa retry quá nhiều
    if (e.target.src !== fallbackImage && !e.target.dataset.retryCount) {
      e.target.dataset.retryCount = '1';
      e.target.src = fallbackImage;
    } else if (e.target.dataset.retryCount && parseInt(e.target.dataset.retryCount) < 2) {
      // Retry một lần nữa với placeholder khác
      e.target.dataset.retryCount = '2';
      e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop&q=80';
    }
    
    // Log để debug (có thể xóa sau)
    if (track && track.title) {
      console.warn(`Failed to load image for track "${track.title}": ${failedUrl}`);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="music-page page-with-header">
      <Header />
      
      {/* Hero Section */}
      <section className="music-hero">
        <div className="music-hero-content">
          <h1 className="music-hero-title">Nhạc chữa lành - Hành trình tìm lại bình yên</h1>
          <p className="music-hero-subtitle">
            Khám phá sức mạnh chữa lành của âm nhạc. Thư giãn, giảm căng thẳng và tìm lại sự bình yên trong tâm hồn 
            với bộ sưu tập nhạc chữa lành được chọn lọc kỹ lưỡng của chúng tôi.
          </p>
          <div className="music-hero-actions">
            <button 
              className="music-hero-btn primary" 
              onClick={handleGetRecommendations}
            >
               Để âm nhạc chọn bạn
            </button>
            <button 
              className="music-hero-btn secondary" 
              onClick={() => {
                setShowRecommendations(false);
                setShowAllTracks(true);
              }}
            >
              📚 Xem tất cả
            </button>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="music-filters">
        <div className="music-filters-content">
          <div className="music-search">
            <input
              type="text"
              placeholder="Tìm kiếm nhạc..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="music-search-input"
            />
            <span className="music-search-icon">🔍</span>
          </div>
          
          <div className="music-filter-group">
            <label>Danh mục:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="music-filter-select"
            >
              <option value="all">Tất cả</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="music-filter-group">
            <label>Nền tảng:</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="music-filter-select"
            >
              <option value="all">Tất cả</option>
              {platforms.map(platform => (
                <option key={platform.id} value={platform.id}>{platform.display_name}</option>
              ))}
            </select>
          </div>

          {/* Favorite Button */}
          {(user || JSON.parse(localStorage.getItem('user') || 'null')) && (
            <button
              className={`music-favorite-menu-btn ${showFavoritesPage ? 'active' : ''}`}
              onClick={() => {
                setShowFavoritesPage(!showFavoritesPage);
                setShowRecommendations(false);
              }}
              title="Xem yêu thích"
            >
              ❤️ Yêu thích {favorites.length > 0 && `(${favorites.length})`}
            </button>
          )}
        </div>
      </section>

      {/* Favorites Page - Only show when showFavoritesPage is true */}
      {showFavoritesPage && (user || JSON.parse(localStorage.getItem('user') || 'null')) && (
        <section className="music-favorites-section">
          <div className="music-section-header">
            <h2>❤️ Yêu thích của bạn {favorites.length > 0 && `(${favorites.length})`}</h2>
            <button 
              className="music-close-recommendations"
              onClick={() => setShowFavoritesPage(false)}
              title="Đóng"
            >
              ✕ Đóng
            </button>
          </div>
          {favorites.length === 0 ? (
            <div className="music-empty" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
              <p>Bạn chưa có bài hát yêu thích nào. Hãy thêm bài hát vào yêu thích bằng cách click vào icon ❤️ trên mỗi bài hát.</p>
            </div>
          ) : (
            <div className="music-tracks-grid">
              {favorites.map(fav => {
                const favTrackId = fav.track_id || fav.music_track_id || fav.musicTrackId;
                const track = tracks.find(t => {
                  const trackId = t.track_id || t.id;
                  return String(trackId) === String(favTrackId);
                });
                if (!track) {
                  console.warn('Track not found for favorite:', fav);
                  return null;
                }
                return (
                  <div key={fav.id || fav.favorite_id} className="music-track-card">
                    <div className="music-track-cover">
                      <img 
                        src={getTrackImage(track)} 
                        alt={track.title}
                        onError={(e) => {
                          // Fallback nếu hình ảnh lỗi
                          e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop';
                        }}
                      />
                    </div>
                    <div className="music-track-info">
                      <h3 className="music-track-title">{track.title}</h3>
                      <p className="music-track-artist">{track.artist}</p>
                      <div className="music-track-meta">
                        <span>{formatDuration(track.duration || track.duration_seconds)}</span>
                      </div>
                    </div>
                    <div className="music-track-actions">
                      <button
                        className="music-favorite-btn favorited"
                        onClick={() => handleToggleFavorite(track)}
                        title="Bỏ yêu thích"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                      </button>
                      <button
                        className={`music-play-btn ${currentPlayingTrack?.id === track.id && isPlaying ? 'playing' : ''}`}
                        onClick={() => handlePlayTrack(track)}
                        title={currentPlayingTrack?.id === track.id && isPlaying ? 'Tạm dừng' : 'Phát'}
                      >
                        {currentPlayingTrack?.id === track.id && isPlaying ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        )}
                        <span style={{ marginLeft: '6px' }}>
                          {currentPlayingTrack?.id === track.id && isPlaying ? 'Tạm dừng' : 'Phát'}
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Recommendations Section */}
      {showRecommendations && recommendations.length > 0 && (
        <section className="music-recommendations">
          <div className="music-section-header">
            <h2>Khám phá dành riêng cho bạn</h2>
            <button 
              className="music-close-recommendations"
              onClick={() => {
                setShowRecommendations(false);
                setShowAllTracks(false); // Hiển thị 10 bài ngẫu nhiên khi đóng recommendations
              }}
            >
              ✕
            </button>
          </div>
          <div className="music-tracks-grid">
            {recommendations.map(track => (
              <div key={track.track_id || track.id} className="music-track-card">
                <div className="music-track-cover">
                  <img 
                    src={getTrackImage(track)} 
                    alt={track.title || 'Music track'}
                    loading="lazy"
                    onError={(e) => handleImageError(e, track)}
                    onLoad={(e) => {
                      // Xóa retry count khi load thành công
                      if (e.target.dataset.retryCount) {
                        delete e.target.dataset.retryCount;
                      }
                    }}
                  />
                </div>
                <div className="music-track-info">
                  <h3 className="music-track-title">{track.title}</h3>
                  <p className="music-track-artist">{track.artist}</p>
                  <div className="music-track-meta">
                    <span>{formatDuration(track.duration)}</span>
                    {track.platform_name && <span className="music-platform-badge">{track.platform_name}</span>}
                  </div>
                </div>
                <div className="music-track-actions">
                  <button
                    className={`music-favorite-btn ${isFavorited(track.track_id || track.id) ? 'favorited' : ''}`}
                    onClick={() => handleToggleFavorite(track)}
                    title={isFavorited(track.track_id || track.id) ? 'Bỏ yêu thích' : 'Thêm vào yêu thích'}
                  >
                    {isFavorited(track.track_id || track.id) ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    )}
                  </button>
                  <button
                    className={`music-play-btn ${currentPlayingTrack?.id === (track.track_id || track.id) && isPlaying ? 'playing' : ''}`}
                    onClick={() => handlePlayTrack(track)}
                    title={currentPlayingTrack?.id === (track.track_id || track.id) && isPlaying ? 'Tạm dừng' : 'Phát'}
                  >
                    {currentPlayingTrack?.id === (track.track_id || track.id) && isPlaying ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                    <span style={{ marginLeft: '6px' }}>
                      {currentPlayingTrack?.id === (track.track_id || track.id) && isPlaying ? 'Tạm dừng' : 'Phát'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* All Tracks Section - Hide when showing favorites page or recommendations */}
      {!showFavoritesPage && !showRecommendations && (
      <section className="music-tracks-section">
        <div className="music-section-header">
          <h2>
            {showRecommendations ? 'Tất cả nhạc' : 'Danh sách nhạc'}
            {getDisplayTracks().length > 0 && (
              <span className="music-count">
                ({getDisplayTracks().length}
                {!showAllTracks && filteredTracks.length > 10 && !searchQuery.trim() && selectedCategory === 'all' && selectedPlatform === 'all' && ` / ${filteredTracks.length}`})
              </span>
            )}
          </h2>
          {!showAllTracks && filteredTracks.length > 10 && !searchQuery.trim() && selectedCategory === 'all' && selectedPlatform === 'all' && (
            <button
              className="music-show-more-btn"
              onClick={() => setShowAllTracks(true)}
            >
              Xem tất cả ({filteredTracks.length} bài)
            </button>
          )}
        </div>

        {loading ? (
          <div className="music-loading">
            <div className="music-spinner"></div>
          <p>Đang tải nhạc...</p>
        </div>
        ) : getDisplayTracks().length === 0 ? (
          <div className="music-empty">
            <p>Không tìm thấy nhạc nào. Hãy thử tìm kiếm khác.</p>
          </div>
        ) : (
          <div className="music-tracks-grid">
            {getDisplayTracks().map(track => (
              <div key={track.id} className="music-track-card">
                <div className="music-track-cover">
                  <img 
                    src={getTrackImage(track)} 
                    alt={track.title || 'Music track'}
                    loading="lazy"
                    onError={(e) => handleImageError(e, track)}
                    onLoad={(e) => {
                      // Xóa retry count khi load thành công
                      if (e.target.dataset.retryCount) {
                        delete e.target.dataset.retryCount;
                      }
                    }}
                  />
                </div>
                <div className="music-track-info">
                  <h3 className="music-track-title">{track.title}</h3>
                  <p className="music-track-artist">{track.artist}</p>
                  {track.description && (
                    <p className="music-track-description">{track.description}</p>
                  )}
                  <div className="music-track-meta">
                    <span>{formatDuration(track.duration || track.duration_seconds)}</span>
                    {track.platform_name && (
                      <span className="music-platform-badge">{track.platform_name}</span>
                    )}
                  </div>
                </div>
                <div className="music-track-actions">
                  <button
                    className={`music-favorite-btn ${isFavorited(track.id) ? 'favorited' : ''}`}
                    onClick={() => handleToggleFavorite(track)}
                    title={isFavorited(track.id) ? 'Bỏ yêu thích' : 'Thêm vào yêu thích'}
                  >
                    {isFavorited(track.id) ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    )}
                  </button>
                  <button
                    className={`music-play-btn ${currentPlayingTrack?.id === track.id && isPlaying ? 'playing' : ''}`}
                    onClick={() => handlePlayTrack(track)}
                    title={currentPlayingTrack?.id === track.id && isPlaying ? 'Tạm dừng' : 'Phát'}
                  >
                    {currentPlayingTrack?.id === track.id && isPlaying ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                    <span style={{ marginLeft: '6px' }}>
                      {currentPlayingTrack?.id === track.id && isPlaying ? 'Tạm dừng' : 'Phát'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      <Footer />
      
      {/* Audio element for playing tracks */}
      <audio 
        ref={audioRef} 
        preload="metadata"
        crossOrigin="anonymous"
      />
      
      {/* Error notification */}
      {playError && (
        <div 
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            background: '#fee2e2',
            color: '#dc2626',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxWidth: '350px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInUp 0.3s ease'
          }}
        >
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span style={{ flex: 1 }}>{playError}</span>
          <button
            onClick={() => setPlayError(null)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#dc2626',
              padding: '0',
              lineHeight: '1'
            }}
            aria-label="Đóng"
          >
            ×
          </button>
        </div>
      )}
      
      {/* Music Player */}
      <MusicPlayer />

      {/* Music Popup - Mini Player */}
      {showPopup && isPopupMinimized && (
        <div 
          className="music-popup-mini"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: '300px',
            maxWidth: '400px',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => setIsPopupMinimized(false)}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#1e293b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {currentPopupTrack?.title || 'Đang phát nhạc'}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#64748b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {currentPopupTrack?.artist || ''}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsPopupMinimized(false);
            }}
            style={{
              background: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#38a169';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#48bb78';
            }}
            aria-label="Mở rộng"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPopup(false);
              setIsPopupMinimized(false);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0',
              lineHeight: '1',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#64748b';
            }}
            aria-label="Đóng"
          >
            ×
          </button>
        </div>
      )}

      {/* Music Popup - Always render iframe to keep it playing */}
      {showPopup && (
        <>
          {/* Hidden iframe container - always rendered to keep playing */}
          <div style={{
            position: 'fixed',
            top: isPopupMinimized ? '-9999px' : 'auto',
            left: isPopupMinimized ? '-9999px' : 'auto',
            width: isPopupMinimized ? '1px' : 'auto',
            height: isPopupMinimized ? '1px' : 'auto',
            overflow: 'hidden',
            zIndex: isPopupMinimized ? -1 : 'auto',
            pointerEvents: 'none',
            opacity: isPopupMinimized ? 0 : 1
          }}>
            {popupType === 'youtube' && (
              <iframe
                width={isPopupMinimized ? '1' : '800'}
                height={isPopupMinimized ? '1' : '450'}
                src={popupUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ borderRadius: '8px' }}
              />
            )}
            
            {popupType === 'spotify' && (
              <iframe
                width={isPopupMinimized ? '1' : '500'}
                height={isPopupMinimized ? '1' : '380'}
                src={popupUrl}
                frameBorder="0"
                allowtransparency="true"
                allow="encrypted-media"
                style={{ borderRadius: '12px' }}
              />
            )}
            
            {popupType === 'soundcloud' && (
              <iframe
                width={isPopupMinimized ? '1' : '600'}
                height={isPopupMinimized ? '1' : '400'}
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(popupUrl)}&color=%23ff5500&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
                style={{ borderRadius: '8px' }}
              />
            )}
          </div>

          {/* Overlay - only show when not minimized */}
          {!isPopupMinimized && (
            <div 
              className="music-popup-overlay"
              onClick={() => setIsPopupMinimized(true)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                padding: '20px'
              }}
            >
              <div 
                className="music-popup-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  width: popupType === 'youtube' ? '800px' : popupType === 'spotify' ? '500px' : '600px',
                  position: 'relative',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  display: 'flex',
                  gap: '8px',
                  zIndex: 10
                }}>
                  <button
                    onClick={() => setIsPopupMinimized(true)}
                    style={{
                      background: '#64748b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.background = '#475569';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.background = '#64748b';
                    }}
                    aria-label="Thu nhỏ"
                    title="Thu nhỏ"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setShowPopup(false);
                      setIsPopupMinimized(false);
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      cursor: 'pointer',
                      fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.background = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.background = '#ef4444';
                    }}
                    aria-label="Đóng"
                  >
                    ×
                  </button>
                </div>
                
                {/* Visible iframe in popup */}
                <div style={{ 
                  position: 'relative',
                  width: '100%',
                  height: 'auto'
                }}>
                  {popupType === 'youtube' && (
                    <iframe
                      width="100%"
                      height="450"
                      src={popupUrl}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ borderRadius: '8px' }}
                    />
                  )}
                  
                  {popupType === 'spotify' && (
                    <iframe
                      width="100%"
                      height="380"
                      src={popupUrl}
                      frameBorder="0"
                      allowtransparency="true"
                      allow="encrypted-media"
                      style={{ borderRadius: '12px' }}
                    />
                  )}
                  
                  {popupType === 'soundcloud' && (
                    <iframe
                      width="100%"
                      height="400"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay"
                      src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(popupUrl)}&color=%23ff5500&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
                      style={{ borderRadius: '8px' }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

